import React, { useEffect, useState } from 'react';
import { onMessage } from 'firebase/messaging';
import { db, auth, messaging } from '../../Context/firebaseConfig';
import { clearNotifications } from '../../api/user';
import { toast } from 'react-toastify';
import { doc, onSnapshot } from 'firebase/firestore';
import { getSafeNextPath } from '../../utils/navigation';
import { BellIcon, CheckCircleIcon, SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropDown, setShowDropDown] = useState(false);

  useEffect(() => {
    let unsubscribeFirestore = null;

    const fetchNotificationsRealtime = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const userDocRef = doc(db, "Users", currentUser.uid);

      unsubscribeFirestore = onSnapshot(userDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          setNotifications(userData.notifications || []);
          setLoading(false);
        }
      });
    };

    fetchNotificationsRealtime();

    const unsubscribeMessaging = onMessage(messaging, (payload) => {
      console.log('FCM payload received:', payload);
      toast.info(payload.notification.body);
    });

    return () => {
      if (unsubscribeFirestore) unsubscribeFirestore();
      unsubscribeMessaging();
    };
  }, []);

  const handleClear = async () => {
    try {
      setNotifications([]);
      await clearNotifications();
      toast.success('Notifications cleared');
    } catch (error) {
      console.error('An error occurred while clearing notifications:', error);
      toast.error('An error occurred while clearing notifications');
    }
  };

  const hasNotifications = notifications.length > 0;

  return (
    <div className="relative flex items-center justify-center">
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setShowDropDown(!showDropDown)}
        className="group relative grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[0.045] text-white/[0.82] shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_10px_35px_-24px_rgba(132,255,0,0.8)] transition hover:border-lime-300/35 hover:bg-lime-300/10 hover:text-lime-200"
      >
        <span className="absolute inset-0 rounded-full bg-gradient-to-br from-white/10 to-transparent opacity-0 transition group-hover:opacity-100" />
        <BellIcon className="relative h-5 w-5" aria-hidden="true" />
        {notifications.length > 0 && (
          <span className="absolute -right-1 -top-1 grid min-h-5 min-w-5 place-items-center rounded-full border border-[#080711] bg-[#84ff00] px-1.5 text-[10px] font-black leading-none text-black shadow-[0_0_18px_rgba(132,255,0,0.55)]">
            {notifications.length > 99 ? "99+" : notifications.length}
          </span>
        )}
      </button>

      {showDropDown && (
        <div className="absolute right-0 top-12 z-[120] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-3xl border border-white/10 bg-[#0a0812] text-white shadow-[0_28px_90px_-35px_rgba(0,0,0,0.9),0_0_0_1px_rgba(132,255,0,0.05)] backdrop-blur-2xl">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#84ff00]/70 to-transparent" />
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3.5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-300">Notifications</p>
              <p className="mt-1 text-sm text-zinc-400">
                {loading ? "Checking for updates" : `${notifications.length} ${notifications.length === 1 ? "notification" : "notifications"}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowDropDown(false)}
              className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 transition hover:bg-white/[0.08] hover:text-white"
              aria-label="Close notifications"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto p-2 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.18)_transparent]">
              {loading ? (
                <div className="grid min-h-32 place-items-center rounded-2xl border border-white/10 bg-white/[0.03] text-sm text-zinc-400">
                  Loading notifications...
                </div>
              ) : notifications.length === 0 ? (
                <div className="grid min-h-40 place-items-center rounded-2xl border border-white/10 bg-white/[0.03] px-6 text-center">
                  <div>
                    <CheckCircleIcon className="mx-auto h-8 w-8 text-lime-300" />
                    <p className="mt-3 text-sm font-semibold text-white">You are all caught up</p>
                    <p className="mt-1 text-xs leading-relaxed text-zinc-500">New orders, campaign updates, and messages will appear here.</p>
                  </div>
                </div>
              ) : (
                <>
                  {notifications.map((notification, index) => (
                    <button
                      key={index}
                      type="button"
                      className="group mb-2 flex w-full items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.035] p-3 text-left transition hover:border-lime-300/25 hover:bg-lime-300/10"
                      onClick={() => {
                        window.location.href = getSafeNextPath(notification.navigationLink, "/");
                        setShowDropDown(false);
                      }}
                    >
                      <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-lime-300/10 text-lime-300 ring-1 ring-lime-300/20 transition group-hover:bg-lime-300/15">
                        <SparklesIcon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-white">{notification.title || "Kre8 update"}</span>
                        <span className="mt-1 line-clamp-2 block text-xs leading-relaxed text-zinc-400">{notification.body}</span>
                      </span>
                    </button>
                  ))}
                  <div className="sticky bottom-0 border-t border-white/10 bg-[#0a0812] p-2 backdrop-blur-xl">
                    <button
                      onClick={handleClear}
                      className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-semibold text-white/[0.82] transition hover:border-red-300/25 hover:bg-red-500/10 hover:text-red-100"
                    >
                      Clear notifications
                    </button>
                  </div>
                </>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
